import { useRef } from "react";
import { Link } from "react-router-dom";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "react-i18next";
import { Calendar, Heart, Star, Video, ExternalLink } from "lucide-react";
import Hero from "../components/Hero";
import CounterSection from "../components/CounterSection";
import PoojaTimingsTable from "../components/PoojaTimingsTable";
import FestivalCard from "../components/FestivalCard";

const SLOGAN_TEXT =
  'ஓம் நமோ பகவதே நரசிம்ஹாய நமஸ் தேஜஸ்-தேஜஸே ஆவிர்-ஆவிர்பவ ✦ வஜ்ர-நக வஜ்ர-தஷ்டிர கர்மசாயன் ரந்தாய ரந்தாய தமோ க்ரஸா கிராசா ஓம் ஸ்வாஹா ✦ அபயம் அபயம் ஆத்மனி பூயிஷ்டா ஓம் க்ரௌம்'

function SloganTicker() {
  return (
    <>
      {/* Fixed strip that sits right below the navbar (h-16 = 64px mobile, h-20 = 80px md+) */}
      <div
        className="fixed top-16 md:top-20 left-0 right-0 z-40 overflow-hidden py-2"
        style={{
          background: 'linear-gradient(90deg,#1A0A00,#2D1500,#1A0A00)',
          borderBottom: '1px solid rgba(255,183,77,0.4)',
        }}
      >
        <div
          className="flex whitespace-nowrap"
          style={{ animation: 'slogan-ticker 40s linear infinite' }}
        >
          {[0, 1, 2].map((i) => (
            <span key={i} className="font-tamil text-gold text-sm tracking-wide px-12 shrink-0">
              {SLOGAN_TEXT}
            </span>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes slogan-ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </>
  )
}


gsap.registerPlugin(ScrollTrigger);

const quickCards = [
  {
    icon: Calendar,
    label: "quickAccess.bookPooja",
    to: "/pooja",
    color: "text-saffron",
  },
  {
    icon: Heart,
    label: "quickAccess.makeDonation",
    to: "/donations",
    color: "text-gold-dark",
  },
  {
    icon: Star,
    label: "quickAccess.festivalCalendar",
    to: "/festivals",
    color: "text-saffron-dark",
  },
  {
    icon: Video,
    label: "quickAccess.liveDarshan",
    to: "https://youtube.com",
    external: true,
    color: "text-charcoal",
  },
];

const upcomingFestivals = [
  {
    name: "Brahmotsavam",
    tamilName: "பிரம்மோத்சவம்",
    month: "April / May",
    duration: "10 Days",
    significance:
      "Grand annual festival of Sri Lakshmi Narasimha Perumal with processions, recitals, and special sevas.",
    details:
      "The 10-day Brahmotsavam includes daily processions of the Lord on different vahanas, Kalyana Utsavam, and grand fireworks on the final day.",
  },
  {
    name: "Vaikunta Ekadasi",
    tamilName: "வைகுண்ட ஏகாதசி",
    month: "December / January",
    duration: "2 Days",
    significance:
      "The most sacred day for Vaishnavites — the gates of Vaikunta (heaven) are said to open on this day.",
    details:
      "Thousands of devotees fast and observe this sacred day. The Lord is taken in a grand procession through the Swarga Vasal (golden gate).",
  },
  {
    name: "Narasimha Jayanthi",
    tamilName: "நரசிம்ஹ ஜயந்தி",
    month: "May / June",
    duration: "1 Day",
    significance:
      "Celebrates the divine incarnation of Lord Narasimha who emerged to protect devotee Prahlada.",
    details:
      "Special abhishekam, alankaram, and homam are performed from dawn. The Prahlada charitra is enacted in the evening.",
  },
];

// Simple SVG kolam divider
function KolamDivider() {
  return (
    <div className="flex items-center justify-center py-2">
      <svg width="300" height="20" viewBox="0 0 300 20" fill="none">
        <line
          x1="0"
          y1="10"
          x2="120"
          y2="10"
          stroke="#FFB74D"
          strokeWidth="1"
          strokeDasharray="4 4"
        />
        <circle
          cx="150"
          cy="10"
          r="6"
          fill="none"
          stroke="#FFB74D"
          strokeWidth="1.5"
        />
        <circle cx="150" cy="10" r="2" fill="#FF6B00" />
        <circle
          cx="130"
          cy="10"
          r="3"
          fill="none"
          stroke="#FFB74D"
          strokeWidth="1"
        />
        <circle
          cx="170"
          cy="10"
          r="3"
          fill="none"
          stroke="#FFB74D"
          strokeWidth="1"
        />
        <line
          x1="180"
          y1="10"
          x2="300"
          y2="10"
          stroke="#FFB74D"
          strokeWidth="1"
          strokeDasharray="4 4"
        />
      </svg>
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const quickRef = useRef(null);
  const aboutRef = useRef(null);
  const festivalsRef = useRef(null);

  useGSAP(() => {
    // Quick access cards
    gsap.fromTo(
      quickRef.current.querySelectorAll(".quick-card"),
      { y: 50, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: "power2.out",
        scrollTrigger: { trigger: quickRef.current, start: "top 85%" },
      }
    );

    // About section
    gsap.fromTo(".about-img",
      { x: -60, opacity: 0 },
      {
        x: 0, opacity: 1, duration: 0.8, ease: "power2.out",
        scrollTrigger: { trigger: aboutRef.current, start: "top 80%" },
      }
    );
    gsap.fromTo(".about-text",
      { x: 60, opacity: 0 },
      {
        x: 0, opacity: 1, duration: 0.8, ease: "power2.out",
        scrollTrigger: { trigger: aboutRef.current, start: "top 80%" },
      }
    );

    // Festival cards
    gsap.fromTo(
      festivalsRef.current.querySelectorAll(".festival-item"),
      { y: 50, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.6, stagger: 0.12, ease: "power2.out",
        scrollTrigger: { trigger: festivalsRef.current, start: "top 85%" },
      }
    );
  }, { scope: containerRef });

  return (
    <main ref={containerRef}>
      <SloganTicker />
      <Hero />

      {/* Quick Access Cards */}
      <section ref={quickRef} className="py-16 bg-cream">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {quickCards.map((card) => {
              const Icon = card.icon;
              const inner = (
                <div className="quick-card bg-white rounded-2xl p-6 border-t-4 border-gold flex flex-col items-center text-center gap-3 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                  <Icon size={32} className={card.color} strokeWidth={1.5} />
                  <span className="font-semibold text-charcoal text-sm">
                    {t(card.label)}
                  </span>
                </div>
              );
              return card.external ? (
                <a
                  key={card.label}
                  href={card.to}
                  target="_blank"
                  rel="noreferrer"
                >
                  {inner}
                </a>
              ) : (
                <Link key={card.label} to={card.to}>
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <KolamDivider />

      {/* About Section */}
      <section ref={aboutRef} className="py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <div className="about-img">
            <div className="relative">
              <img
                src="/galary/about.png"
                alt="Temple"
                className="rounded-2xl w-full object-cover h-96 shadow-xl"
              />
              <div className="absolute inset-0 rounded-2xl ring-4 ring-gold/30 ring-offset-4 ring-offset-cream" />
            </div>
          </div>
          <div className="about-text space-y-5">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-charcoal">
              {t("about.heading")}
            </h2>
            <p className="text-charcoal-soft leading-relaxed">
              ஸ்ரீ லட்சுமி நரசிம்ஹ பெருமாள் ஆலயம், கெங்கநல்லூர் — a sacred abode
              where Lord Narasimha (the lion-man avatar of Lord Vishnu) graces
              His devotees alongside Goddess Lakshmi. This ancient temple is a
              beacon of faith, devotion, and tradition for all Vaishnavites of
              the region.
            </p>
            <p className="text-charcoal-soft leading-relaxed">
              The presiding deity, Sri Lakshmi Narasimha Perumal, bestows His
              devotees with protection, prosperity, and liberation. The temple
              observes all traditional Pancharatra Agama rituals every day,
              drawing thousands of devotees who seek the Lord's divine grace.
            </p>
            <blockquote className="border-l-4 border-saffron pl-4 py-2">
              <p className="font-tamil text-saffron text-lg">
                {t("about.quote")}
              </p>
            </blockquote>
            <Link
              to="/history"
              className="inline-flex items-center gap-2 text-saffron font-semibold hover:text-saffron-dark transition-colors"
            >
              {t("about.readMore")} →
            </Link>
          </div>
        </div>
      </section>

      <CounterSection />

      <KolamDivider />

      {/* Pooja Timings */}
      <section className="py-16 bg-cream">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-display text-3xl font-bold text-charcoal text-center mb-3">
            {t("timings.title")}
          </h2>
          <p className="text-charcoal-soft text-center mb-8">
            The temple is open for worship every day of the year
          </p>
          <PoojaTimingsTable />
        </div>
      </section>

      <KolamDivider />

      {/* Upcoming Festivals */}
      <section ref={festivalsRef} className="py-16 bg-cream">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-display text-3xl font-bold text-charcoal">
                Upcoming Festivals
              </h2>
              <p className="font-tamil text-gold mt-1">
                வரவிருக்கும் திருவிழாக்கள்
              </p>
            </div>
            <Link
              to="/festivals"
              className="text-saffron font-semibold hover:text-saffron-dark transition-colors hidden sm:block"
            >
              View All →
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {upcomingFestivals.map((f) => (
              <div key={f.name} className="festival-item">
                <FestivalCard festival={f} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer Banner */}
      <section className="bg-saffron py-10">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-tamil text-white text-xl mb-1">
              ஓம் நமோ நாராயணாய
            </p>
            <p className="text-white/70 text-sm">
              Sacred Namam of Lord Narasimha — Blessings of the Lord upon all
              devotees
            </p>
          </div>
          <a
            href="https://wa.me/919786714972"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 bg-white text-saffron font-semibold px-6 py-3 rounded-full hover:shadow-lg transition-all shrink-0"
          >
            WhatsApp Us
          </a>
        </div>
      </section>
    </main>
  );
}
